import { z } from "zod";

export const APPLICATION_WORKSPACE_SECTION_IDS = [
  "overview",
  "sign-in",
  "assignments",
  "access-requests",
  "diagnostics",
] as const;

export type ApplicationWorkspaceSection =
  (typeof APPLICATION_WORKSPACE_SECTION_IDS)[number];

export const applicationWorkspaceSearchSchema = z.object({
  section: z.enum(APPLICATION_WORKSPACE_SECTION_IDS).optional(),
});

export function visibleApplicationWorkspaceSections(input: {
  canViewAssignments: boolean;
  canViewAccessRequests: boolean;
  canRunDiagnostics: boolean;
}): ApplicationWorkspaceSection[] {
  return APPLICATION_WORKSPACE_SECTION_IDS.filter(
    (id) =>
      (id !== "access-requests" || input.canViewAccessRequests) &&
      (id !== "assignments" || input.canViewAssignments) &&
      (id !== "diagnostics" || input.canRunDiagnostics),
  );
}

export function normalizeApplicationWorkspaceSection(
  requested: ApplicationWorkspaceSection,
  input: {
    canViewAssignments: boolean;
    canViewAccessRequests: boolean;
    canRunDiagnostics: boolean;
  },
): ApplicationWorkspaceSection {
  return visibleApplicationWorkspaceSections(input).includes(requested)
    ? requested
    : "overview";
}

export function shouldBlockApplicationNavigation(input: {
  canManage: boolean;
  isDirty: boolean;
  needsNameIDCommit: boolean;
  updatePending: boolean;
}) {
  return (
    input.canManage &&
    (input.isDirty || input.needsNameIDCommit) &&
    !input.updatePending
  );
}

export function canManageApplicationAssignments(
  permissions: readonly string[],
) {
  const permissionSet = new Set(permissions);
  const can = (permission: string) =>
    permissionSet.has(permission) || permissionSet.has(`${permission}!`);
  return (
    can("applications.read") &&
    can("applications.manage") &&
    can("application_assignments.manage") &&
    can("users.read")
  );
}
